import React from 'react';
import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import {
  notEmptyString,
} from '@populateui/utils';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import MailIcon from '@mui/icons-material/MailOutlineRounded';
import {
  useNavigate,
} from 'react-router-dom';
import Skeleton from 'core/components/Skeleton';
import {
  DateRange,
} from 'types/dateRange';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import TempoActivityTimeline from 'pages/Dashboard/pages/Dashboard/Tempo/Details/TempoActivityTimeline';
import useGetTempoPatientDetail from 'pages/Dashboard/pages/Dashboard/Tempo/hooks';
import {
  getDisplayName,
  getInitials,
} from 'pages/Dashboard/utils/helper';
import {
  formatDate,
  formatMonth,
} from 'utils/date';
import {
  getAge,
} from 'utils/misc';

type Props = {
  patientId: number;
  dateRange: DateRange;
  showPatientSummary?: boolean;
  onNavigateAway?: () => void;
};

function getDateOfBirthLabel(dateOfBirth?: string | null): string | null {
  const age = getAge(dateOfBirth ?? '', 'year').value;

  return notEmptyString(dateOfBirth) ? `${formatDate(dateOfBirth ?? '')} (${age})` : null;
}

function TimelineSkeletonCard() {
  return (
    <section className="flex min-h-[300px] min-w-0 flex-col rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <Skeleton variant="rounded" className="!h-5 !w-32" />
        <Skeleton variant="rounded" className="!h-6 !w-20 !rounded-full" />
      </div>
      <div className="mt-4 flex flex-col">
        {['sent', 'completed'].map((itemKey) => (
          <div key={itemKey} className="flex gap-3">
            <div className="flex shrink-0 flex-col items-center">
              <Skeleton variant="circular" className="!h-6 !w-6" />
              {itemKey === 'sent' && <div className="w-px flex-1 bg-gray-200" />}
            </div>
            <div className="-mt-1 flex min-w-0 flex-1 flex-col pb-5 pr-4">
              <Skeleton variant="rounded" className="!h-[15px] !w-28" />
              <Skeleton variant="rounded" className="mt-1 !h-4 !w-40 !max-w-full" />
              <div className="mt-1 flex items-center gap-2">
                <Skeleton variant="rounded" className="!h-5 !w-16 !rounded-full" />
                {itemKey === 'sent' && <Skeleton variant="rounded" className="!h-3 !w-12" />}
              </div>
              {itemKey === 'completed' && (
                <div className="mt-3">
                  <Skeleton variant="rounded" className="!h-[15px] !w-32" />
                  <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh'].map((scoreKey) => (
                      <div
                        key={scoreKey}
                        className="flex min-h-[40px] min-w-0 items-center justify-between gap-2 rounded-md border border-blue-100 bg-blue-50/50 px-2.5 py-2"
                      >
                        <Skeleton variant="rounded" className="!h-4 !w-16 !max-w-full" />
                        <Skeleton variant="rounded" className="!h-5 !w-5 shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

type DetailSkeletonProps = {
  showPatientSummary: boolean;
};

function DetailSkeleton({ showPatientSummary }: DetailSkeletonProps) {
  return (
    <div className="flex min-h-0 w-full min-w-full flex-1 flex-col gap-4">
      {showPatientSummary && (
        <section className="flex w-full min-w-full flex-wrap items-center gap-5 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex min-w-[260px] flex-1 items-center gap-3">
            <Skeleton variant="circular" className="!h-11 !w-11 shrink-0" />
            <div className="flex min-w-0 flex-1 flex-col">
              <Skeleton variant="rounded" className="!h-5 !w-48" />
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <Skeleton variant="rounded" className="!h-[15px] !w-28" />
                <Skeleton variant="rounded" className="!h-[15px] !w-24" />
              </div>
            </div>
          </div>
        </section>
      )}
      <div className="tempo-detail-timelines grid w-full min-w-full grid-cols-2 gap-4">
        <TimelineSkeletonCard />
        <TimelineSkeletonCard />
      </div>
    </div>
  );
}

export default function TempoPatientDetailContent({
  patientId,
  dateRange,
  showPatientSummary = true,
  onNavigateAway,
}: Props) {
  const navigate = useNavigate();
  const {
    data: patientDetail,
    isFetching,
    isError,
  } = useGetTempoPatientDetail(patientId, dateRange);
  const patientChartPath = getPatientChartPath(patientId);
  const { patient } = patientDetail ?? {};
  const patientName = getDisplayName(patient?.formattedNames);
  const dateOfBirth = getDateOfBirthLabel(patient?.dateOfBirth);

  return (
    <div className="tempo-detail-content flex min-h-0 w-full flex-auto overflow-y-auto bg-gray-50 p-5">
      {(isFetching && isNil(patientDetail)) ? (
        <DetailSkeleton showPatientSummary={showPatientSummary} />
      ) : (
        <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
          {isError ? (
            <div className="flex h-40 w-full items-center justify-center text-sm text-red-600">
              Unable to load Tempo patient details.
            </div>
          ) : (
            <>
              {showPatientSummary && (
                <section className="flex flex-wrap items-center gap-5 rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex min-w-[260px] flex-1 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-700">
                      {getInitials(patient?.formattedNames)}
                    </div>
                    <div className="min-w-0">
                      {notEmptyString(patientChartPath) ? (
                        <a
                          href={patientChartPath}
                          onClick={(event) => {
                            event.preventDefault();
                            onNavigateAway?.();
                            navigate(patientChartPath ?? '');
                          }}
                          className="truncate text-sm font-semibold text-blue-600 hover:underline"
                        >
                          {patientName}
                        </a>
                      ) : (
                        <div className="truncate text-sm font-semibold text-gray-900">
                          {patientName}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-500">
                        {dateOfBirth && <span>DOB {dateOfBirth}</span>}
                        {notEmptyString(patient?.phone) && (
                          <span className="flex items-center gap-1" aria-label="Phone">
                            <PhoneRoundedIcon className="!h-3 !w-3 shrink-0" />
                            {patient?.phone}
                          </span>
                        )}
                        {notEmptyString(patient?.email) && (
                          <span className="flex min-w-0 items-center gap-1" aria-label="Email">
                            <MailIcon className="!h-3 !w-3 shrink-0" />
                            <span className="min-w-0 break-words">{patient?.email}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              )}
              <div className="tempo-detail-timelines grid grid-cols-2 gap-4">
                {(patientDetail?.communiques?.length ?? 0) > 0
                  ? patientDetail?.communiques?.map((communique) => (
                    <TempoActivityTimeline
                      key={communique.communiqueId}
                      title={`${startCase(communique.type ?? 'Survey')} Timeline`}
                      status={communique.status}
                      activities={communique.activities ?? []}
                    />
                  )) : (
                    <div className="col-span-full rounded-lg border border-gray-200 bg-white p-5 text-center text-xs text-gray-500">
                      No Tempo activity in {formatMonth(dateRange.min)}.
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
